/**
 * printService.js
 * Impression EasyFacture — quatre chemins :
 *   • Android (Capacitor) : VPS render → PrintPdfPlugin → PrintManager → dialogue HP Print Service
 *   • VPS direct IPP : VPS rend le PDF ET l'envoie directement à l'imprimante réseau (IP publique)
 *   • VPS API + Agent local : VPS rend le PDF, agent Windows l'imprime
 *   • VPS API + Agent LAN : même chose mais l'agent est sur le réseau local (Android)
 */

import { registerPlugin, Capacitor } from '@capacitor/core';

// Pont JS↔natif vers le plugin Android TcpPrintPlugin (TCP socket direct)
const TcpPrint = registerPlugin('TcpPrint');
// Pont JS↔natif vers le plugin Android UsbPrintPlugin (USB OTG direct)
export const UsbPrint = registerPlugin('UsbPrint');
// Pont JS↔natif vers le plugin Android PrintPdfPlugin (PrintManager A4 natif)
const PrintPdf = registerPlugin('PrintPdf');

const SERVICE_URL          = 'https://api.easyfacture.easyclick-solution.fr';
const AGENT_URL_DEFAULT    = 'http://127.0.0.1:9101';
const PRINT_PREFS_KEY      = 'print_prefs';
const NETWORK_PRINTERS_KEY = 'network_printers';
const CHECK_INTERVAL       = 30000;

// ── Imprimantes réseau ─────────────────────────────────────────────────────────
export function getNetworkPrinters() {
  try { return JSON.parse(localStorage.getItem(NETWORK_PRINTERS_KEY) || '[]'); } catch { return []; }
}
export function saveNetworkPrinters(list) {
  localStorage.setItem(NETWORK_PRINTERS_KEY, JSON.stringify(list));
}

// Résout le nom d'imprimante :
//   • Nom enregistré dans "Imprimantes réseau" → IP (l'agent gère IPP/TCP)
//   • Nom Windows / vide → passé tel quel à SumatraPDF
function resolveNetworkPrinter(printerName) {
  if (!printerName) return printerName;
  if (/^tcp:/i.test(printerName)) return printerName;
  const np = getNetworkPrinters().find(p => p.name === printerName);
  if (np) {
    return np.port === 9100 ? `tcp:${np.ip}:9100` : `${np.ip}:${np.port || 631}`;
  }
  return printerName;
}

// Retourne { ip, port } si le nom correspond à une imprimante réseau IPP (pas RAW ESC/POS)
// Sinon retourne null (imprimante Windows ou thermique RAW)
function getNetworkPrinterIppInfo(printerName) {
  if (!printerName) return null;
  // Format direct IP:port ou IP seule
  const m = printerName.match(/^(\d{1,3}(?:\.\d{1,3}){3})(?::(\d+))?$/);
  if (m) return { ip: m[1], port: parseInt(m[2] || '631', 10) };
  // Nom sauvegardé dans "Imprimantes réseau" (port !== 9100 → IPP)
  const np = getNetworkPrinters().find(p => p.name === printerName);
  if (np && np.port !== 9100) return { ip: np.ip, port: np.port || 631 };
  return null;
}

// ── Détection plateforme ───────────────────────────────────────────────────────
export function isAndroid() {
  try { return Capacitor.getPlatform() === 'android'; } catch {}
  return !!(typeof window !== 'undefined' && (
    window.Capacitor?.platform === 'android' ||
    window.Capacitor?.getPlatform?.() === 'android'
  ));
}

// URL de l'agent (localhost par défaut, configurable pour accès LAN / Android)
export function getAgentUrl() {
  try {
    const prefs = JSON.parse(localStorage.getItem(PRINT_PREFS_KEY) || '{}');
    const url = (prefs.lanAgentUrl || '').trim().replace(/\/$/, '');
    return url || AGENT_URL_DEFAULT;
  } catch {
    return AGENT_URL_DEFAULT;
  }
}

// ── Verrou d'impression — empêche deux impressions simultanées ────────────────
let _printing = false;

export function isPrinting() { return _printing; }

// ─────────────────────────────────────────────────────────────────────────────
// VPS Print Service (9101) — disponibilité + rendu PDF
// ─────────────────────────────────────────────────────────────────────────────

let _serviceAvailable = null;
let _serviceLastCheck = 0;

export async function isPrintServiceAvailable(force = false) {
  const now = Date.now();
  if (!force && _serviceAvailable !== null && now - _serviceLastCheck < CHECK_INTERVAL) return _serviceAvailable;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 3000);
    const r = await fetch(`${SERVICE_URL}/status`, { signal: ctrl.signal });
    clearTimeout(t);
    _serviceAvailable = r.ok;
  } catch {
    _serviceAvailable = false;
  }
  _serviceLastCheck = Date.now();
  return _serviceAvailable;
}

export function resetServiceCache() {
  _serviceAvailable = null;
  _serviceLastCheck = 0;
}

// Rendu HTML → PDF + impression IPP directe depuis le VPS (sans agent local)
// Fonctionne si l'imprimante est accessible depuis internet (IP publique ou redirection de port)
export async function renderOnVPSAndPrint({ html, type = 'a4', paperWidth = 80, labelWidth, labelHeight, printerIp, printerPort = 631 }) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 60000);
  try {
    const r = await fetch(`${SERVICE_URL}/render-and-print`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ html, type, paperWidth, labelWidth, labelHeight, printerIp, printerPort }),
      signal:  ctrl.signal,
    });
    clearTimeout(t);
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${r.status}`);
    }
    return await r.json();
  } catch (err) {
    clearTimeout(t);
    throw err;
  }
}

// Rendu HTML → PDF via Puppeteer sur le VPS — retourne base64
export async function renderOnVPS({ html, type = 'a4', paperWidth = 80, labelWidth, labelHeight }) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 45000);
  try {
    const r = await fetch(`${SERVICE_URL}/render`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ html, type, paperWidth, labelWidth, labelHeight }),
      signal:  ctrl.signal,
    });
    clearTimeout(t);
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${r.status}`);
    }
    const { pdf } = await r.json();
    return pdf; // base64 string
  } catch (err) {
    clearTimeout(t);
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Print Agent (local ou LAN) — disponibilité + impression PDF
// ─────────────────────────────────────────────────────────────────────────────

let _agentAvailable = null;
let _agentLastCheck = 0;
let _agentLastUrl   = '';

export async function isAgentAvailable(force = false) {
  const agentUrl = getAgentUrl();
  const now = Date.now();
  // Invalide le cache si l'URL a changé
  if (_agentLastUrl !== agentUrl) { _agentAvailable = null; _agentLastUrl = agentUrl; }
  if (!force && _agentAvailable !== null && now - _agentLastCheck < CHECK_INTERVAL) return _agentAvailable;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 3000);
    const r = await fetch(`${agentUrl}/status`, { signal: ctrl.signal });
    clearTimeout(t);
    _agentAvailable = r.ok;
  } catch {
    _agentAvailable = false;
  }
  _agentLastCheck = Date.now();
  return _agentAvailable;
}

// Ouvre le tiroir de caisse via l'agent
export async function openCashDrawer(printerName) {
  const agentUrl = getAgentUrl();
  const resolvedPrinter = resolveNetworkPrinter(printerName || '');
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 5000);
  try {
    const r = await fetch(`${agentUrl}/drawer`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ printer: resolvedPrinter }),
      signal:  ctrl.signal,
    });
    clearTimeout(t);
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${r.status}`);
    }
    return await r.json().catch(() => ({ success: true }));
  } catch (err) {
    clearTimeout(t);
    throw err;
  }
}

// Envoie un PDF (base64) à l'agent pour impression
export async function printWithAgent({ pdf, printer, landscape = false }) {
  const agentUrl = getAgentUrl();
  const resolvedPrinter = resolveNetworkPrinter(printer || '');
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 15000);
  try {
    const r = await fetch(`${agentUrl}/print`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ pdf, printer: resolvedPrinter, landscape }),
      signal:  ctrl.signal,
    });
    clearTimeout(t);
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${r.status}`);
    }
    return await r.json().catch(() => ({ success: true }));
  } catch (err) {
    clearTimeout(t);
    throw err;
  }
}

// Récupère la liste des ports série (COM) disponibles sur la machine (agent local requis)
export async function getSerialPorts() {
  try {
    const ok = await isAgentAvailable();
    if (!ok) return [];
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 3000);
    const r = await fetch(`${getAgentUrl()}/serial-ports`, { signal: ctrl.signal });
    clearTimeout(t);
    if (!r.ok) return [];
    const data = await r.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

// Récupère la liste des imprimantes (agent en priorité, sinon service VPS)
export async function getPrintersFromService() {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 3000);
    const url = (await isAgentAvailable()) ? `${getAgentUrl()}/printers` : `${SERVICE_URL}/printers`;
    const r = await fetch(url, { signal: ctrl.signal });
    clearTimeout(t);
    if (!r.ok) return [];
    const data = await r.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Android : impression via le dialogue système (WebView Chromium)
// Android WebView ne supporte window.print() que sur le contexte principal —
// l'approche iframe échoue silencieusement. On injecte le contenu dans un
// overlay plein écran, on appelle window.print(), puis on restore.
// ─────────────────────────────────────────────────────────────────────────────

export function printHtmlOnAndroid(html) {
  return new Promise((resolve, reject) => {
    // Injecter les styles de la page à imprimer dans le <head> principal
    const styleId = '__ef_print_style__';
    const overlayId = '__ef_print_overlay__';

    // Extraire styles inline + liens depuis le HTML
    const headMatch = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
    const headContent = headMatch ? headMatch[1] : '';
    const bodyMatch  = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    const bodyContent = bodyMatch ? bodyMatch[1] : html;

    // Supprimer les scripts CDN (non nécessaires pour l'impression, remplacés par Tailwind déjà chargé)
    const safeHead = headContent.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '');

    // Injecter les styles d'impression dans le head principal
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement('div');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    styleEl.innerHTML = safeHead + `<style>
      @media print {
        body > *:not(#${overlayId}) { display: none !important; }
        #${overlayId} { display: block !important; position: static !important; }
        @page { size: A4; margin: 10mm; }
      }
    </style>`;

    // Créer l'overlay de contenu
    const overlay = document.createElement('div');
    overlay.id = overlayId;
    overlay.style.cssText = 'display:none;position:fixed;inset:0;z-index:99999;background:white;overflow:auto;';
    overlay.innerHTML = bodyContent;
    document.body.appendChild(overlay);

    const cleanup = () => {
      overlay.remove();
      styleEl.remove();
    };

    // Laisser le temps au navigateur de parser le contenu avant d'imprimer
    setTimeout(() => {
      try {
        window.print();
        // Nettoyage après fermeture du dialogue (délai heuristique)
        setTimeout(cleanup, 3000);
        resolve();
      } catch (err) {
        cleanup();
        reject(err);
      }
    }, 400);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Flux complet : VPS rend le PDF, agent local l'imprime
// ─────────────────────────────────────────────────────────────────────────────

export async function renderAndPrint({ html, type = 'a4', paperWidth = 80, labelWidth, labelHeight, printerName }) {
  if (_printing) throw new Error('Impression déjà en cours, veuillez patienter…');
  _printing = true;
  const landscape = type === 'label' && labelWidth > labelHeight;
  try {
    if (isAndroid()) {
      // Android avec agent LAN configuré → impression silencieuse sans dialogue
      const agentUrl = getAgentUrl();
      if (agentUrl !== AGENT_URL_DEFAULT) {
        const agentOk = await isAgentAvailable();
        if (agentOk) {
          let pdf;
          try { pdf = await renderOnVPS({ html, type, paperWidth, labelWidth, labelHeight }); }
          catch (err) { throw new Error(`[Render VPS] ${err.message}`); }
          try { await printWithAgent({ pdf, printer: printerName, landscape }); return; }
          catch (err) { throw new Error(`[Agent print] ${err.message}`); }
        }
      }
      // Android sans agent LAN → rendu VPS → PrintManager Android (dialogue HP Print Service Plugin)
      const pdf = await renderOnVPS({ html, type, paperWidth, labelWidth, labelHeight });
      await PrintPdf.print({ data: pdf });
      return;
    }

    // Imprimante réseau IPP → essaie VPS direct en premier (sans agent local)
    const npInfo = getNetworkPrinterIppInfo(printerName);
    if (npInfo) {
      try {
        await renderOnVPSAndPrint({ html, type, paperWidth, labelWidth, labelHeight, printerIp: npInfo.ip, printerPort: npInfo.port });
        return;
      } catch {
        // VPS ne peut pas atteindre l'imprimante LAN → fallback agent local
      }
    }

    // Desktop Windows : VPS render → agent local
    let pdf;
    try {
      pdf = await renderOnVPS({ html, type, paperWidth, labelWidth, labelHeight });
    } catch (err) {
      throw new Error(`[Render VPS] ${err.message}`);
    }
    try {
      await printWithAgent({ pdf, printer: printerName, landscape });
    } catch (err) {
      throw new Error(`[Agent print] ${err.message}`);
    }
  } finally {
    _printing = false;
  }
}

// Impression A4 : toujours le dialogue natif du navigateur, aucune communication
// avec l'agent local ni le service de rendu VPS — les pages InvoiceView/QuoteView/
// DeliveryForm/ReturnForm ont leur propre CSS @media print, donc window.print()
// suffit et fonctionne aussi dans le contexte principal d'une WebView Android.
export async function printA4Dialog() {
  window.print();
}

// ── ESC/POS (imprimantes thermiques TCP RAW port 9100) ────────────────────────

// Mots-clés typiques dans les noms d'imprimantes thermiques installées sous Windows
const ESCPOS_KEYWORDS = ['tm-t', 'tsp', 'pos-', 'pos80', 'pos58', 'thermal', 'receipt', 'ticket', 'bixolon', 'citizen', 'star mic', 'sewoo'];

// Détecte si une imprimante utilise le protocole ESC/POS (TCP RAW ou Windows thermique)
export function isEscPosPrinter(printerName) {
  if (!printerName) return false;
  if (/^tcp:/i.test(printerName)) return true;
  const resolved = resolveNetworkPrinter(printerName);
  if (/^tcp:/i.test(resolved)) return true;
  const np = getNetworkPrinters().find(p => p.name === printerName);
  if (np && np.port === 9100) return true;
  // Adresse IP directe (ex: "192.168.64.20" ou "192.168.64.20:9100") → TCP RAW ESC/POS
  if (/^\d{1,3}(?:\.\d{1,3}){3}(?::\d+)?$/.test(printerName)) return true;
  // Détection par mots-clés pour imprimantes Windows installées (ex: "EPSON TM-T20II Receipt")
  const lower = printerName.toLowerCase();
  return ESCPOS_KEYWORDS.some(kw => lower.includes(kw));
}

// Uint8Array → base64 — chunks de 8 Ko pour éviter stack overflow et O(n²) string concat
export function uint8ToBase64(bytes) {
  const CHUNK = 8192;
  let bin = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

// Génère un ticket de test ESC/POS minimal avec coupe papier
function buildEscPosTestBytes(paperWidth = 80) {
  const ESC = 0x1B, GS = 0x1D;
  const cols = paperWidth === 58 ? 32 : 42;
  const buf = [];
  const t = (str) => { for (const c of str) buf.push(c.charCodeAt(0) < 128 ? c.charCodeAt(0) : 63); };
  const n = (str) => { t(str); buf.push(0x0A); };
  const sep = () => n('-'.repeat(cols));
  const ctr = (str) => { const s = str.slice(0, cols); const p = Math.max(0, Math.floor((cols - s.length) / 2)); n(' '.repeat(p) + s); };
  buf.push(ESC, 0x40);        // Init
  buf.push(ESC, 0x61, 0x01);  // Center
  buf.push(ESC, 0x45, 0x01);  // Bold
  n('EasyFacture'); n('Test Impression');
  buf.push(ESC, 0x45, 0x00);
  sep();
  buf.push(ESC, 0x61, 0x00);  // Left
  n('Article test');
  n('  1.000 x 1.000       1.000');
  sep();
  buf.push(ESC, 0x45, 0x01);
  n('TOTAL          1.000 EUR');
  buf.push(ESC, 0x45, 0x00);
  sep();
  buf.push(ESC, 0x61, 0x01);
  ctr(new Date().toLocaleString('fr-FR'));
  buf.push(ESC, 0x64, 0x04);  // Feed 4 lines
  buf.push(GS, 0x56, 0x00);   // Full cut
  return new Uint8Array(buf);
}

// Envoie des bytes ESC/POS bruts :
//  • Android → socket TCP natif direct (plugin Capacitor TcpPrint)
//  • Desktop  → agent local /print-escpos (SumatraPDF / TCP RAW)
export async function printEscPosWithAgent(escposBytes, printerName) {
  const resolvedPrinter = resolveNetworkPrinter(printerName || '');

  // Sur Android : connexion TCP directe via plugin natif TcpPrint
  if (isAndroid()) {
    let host, port;
    if (/^tcp:/i.test(resolvedPrinter)) {
      const parts = resolvedPrinter.replace(/^tcp:/i, '').split(':');
      host = parts[0];
      port = parseInt(parts[1] || '9100', 10);
    } else {
      const ipPort = resolvedPrinter.match(/^(\d{1,3}(?:\.\d{1,3}){3})(?::(\d+))?$/);
      if (ipPort) { host = ipPort[1]; port = parseInt(ipPort[2] || '9100', 10); }
    }
    if (host) {
      const base64 = uint8ToBase64(escposBytes);
      return TcpPrint.print({ host, port, data: base64 });
    }
  }

  // Desktop Windows ou Android sans plugin → agent /print-escpos
  const agentUrl = getAgentUrl();
  const base64 = uint8ToBase64(escposBytes);
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 15000);
  try {
    const r = await fetch(`${agentUrl}/print-escpos`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ data: base64, printer: resolvedPrinter }),
      signal:  ctrl.signal,
    });
    clearTimeout(t);
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${r.status}`);
    }
    return await r.json().catch(() => ({ success: true }));
  } catch (err) {
    clearTimeout(t);
    throw err;
  }
}

// Impression ticket : rendu VPS + agent local (pour imprimantes PDF/Windows)
export async function printTicketSilent(html, printerName, paperWidth = 80) {
  return renderAndPrint({ html, type: 'receipt', paperWidth, printerName });
}

// Impression étiquette Zebra : rendu VPS + agent local
export async function printLabelSilent(html, printerName, widthMm = 100, heightMm = 150) {
  return renderAndPrint({ html, type: 'label', labelWidth: widthMm, labelHeight: heightMm, printerName });
}

// Test d'impression via VPS + agent
export async function testPrint({ printerName, type = 'a4', paperWidth = 80, labelWidth = 100, labelHeight = 150 } = {}) {
  // ESC/POS : test direct sans rendu PDF (pour imprimantes thermiques TCP port 9100)
  if (type === 'receipt' && isEscPosPrinter(printerName)) {
    const escBytes = buildEscPosTestBytes(paperWidth);
    await printEscPosWithAgent(escBytes, printerName);
    return { success: true };
  }

  const isReceipt = type === 'receipt';
  const isLabel   = type === 'label';
  const w = paperWidth === 58 ? '58mm' : '80mm';
  const html = isReceipt
    ? `<!DOCTYPE html><html><head><meta charset="utf-8">
        <style>body{font-family:monospace;font-size:12px;width:${w};margin:0;padding:4mm}
        h2{text-align:center;font-size:14px}hr{border:1px dashed #000}
        .row{display:flex;justify-content:space-between}</style></head>
        <body><h2>EasyFacture</h2><h2>Test Impression</h2><hr>
        <p class="row"><span>Article test</span><span>1,00 €</span></p><hr>
        <p class="row"><b>TOTAL</b><b>1,00 €</b></p><hr>
        <p style="text-align:center;font-size:10px">Ticket de test — ${new Date().toLocaleString('fr-FR')}</p>
        </body></html>`
    : isLabel
    ? `<!DOCTYPE html><html><head><meta charset="utf-8">
        <style>
          @page{margin:0;size:${labelWidth}mm ${labelHeight}mm}
          *{margin:0;padding:0;box-sizing:border-box}
          body{font-family:Arial,sans-serif;width:${labelWidth}mm;height:${labelHeight}mm;
               padding:3mm;color:#000;overflow:hidden}
          .top{font-size:7px;color:#888;text-transform:uppercase;letter-spacing:.5px;margin-bottom:2mm}
          .name{font-size:13px;font-weight:bold;margin-bottom:1mm}
          .ref{font-size:8px;color:#555;margin-bottom:2mm}
          .price{font-size:18px;font-weight:bold;border-top:1px dashed #ccc;padding-top:2mm}
          .date{font-size:7px;color:#aaa;margin-top:2mm}
        </style></head>
        <body>
          <div class="top">EasyFacture — Test étiquette</div>
          <div class="name">Produit exemple</div>
          <div class="ref">Réf. EF-TEST-001 · ${labelWidth}×${labelHeight}mm</div>
          <div class="price">29,90 €</div>
          <div class="date">${new Date().toLocaleString('fr-FR')}</div>
        </body></html>`
    : `<!DOCTYPE html><html><head><meta charset="utf-8">
        <style>body{font-family:Arial,sans-serif;padding:40px;color:#333}
        h1{color:#059669}table{width:100%;border-collapse:collapse;margin-top:20px}
        th,td{border:1px solid #ddd;padding:8px}th{background:#f0fdf4}</style></head>
        <body><h1>EasyFacture — Page de test A4</h1>
        <p>Ce document confirme que le service d'impression fonctionne correctement.</p>
        <table><tr><th>Élément</th><th>Statut</th></tr>
        <tr><td>Connexion VPS</td><td>OK</td></tr>
        <tr><td>Rendu PDF</td><td>OK</td></tr>
        <tr><td>Agent local (port 9101)</td><td>OK</td></tr></table>
        <p style="margin-top:30px;font-size:12px;color:#888">Imprimé le ${new Date().toLocaleString('fr-FR')}</p>
        </body></html>`;

  await renderAndPrint({ html, type, paperWidth, labelWidth, labelHeight, printerName });
  return { success: true };
}
